import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../common/LanguageSwitcher';
import UserAvatar from '../common/UserAvatar';
import './BusinessPublicProfile.css';

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function BusinessPublicProfile() {
  const { businessId } = useParams();
  const { currentUser } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!businessId) return;

    const loadProfile = async () => {
      try {
        const token = currentUser ? await currentUser.getIdToken() : null;
        const res = await fetch(
          `${API_BASE_URL}/api/business/profile/${businessId}/public`,
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`
                }
              : {}
          }
        );
        if (!res.ok) {
          throw new Error('Failed to load business profile');
        }
        const json = await res.json();
        setProfile(json);
      } catch (e) {
        console.error('Failed to load public business profile', e);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [businessId, currentUser]);

  if (loading) {
    return (
      <div className="business-public-container">
        <div className="business-public-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="business-public-container">
        <div className="business-public-loading">
          {t('businessPublic.notFound') || 'Business not found'}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`business-public-container ${isRTL ? 'rtl' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <header className="business-public-header">
        <div className="business-public-header-content">
          <LanguageSwitcher />
          <button
            className="business-public-back"
            onClick={() => navigate(-1)}
          >
            {t('common.back') || 'Back'}
          </button>
        </div>
      </header>

      <main className="business-public-main">
        <section className="business-public-hero">
          <div>
            {profile.image_url ? (
              <img
                src={profile.image_url}
                alt={profile.name}
                className="business-public-image"
              />
            ) : (
              <UserAvatar
                user={{ uid: businessId, displayName: profile.name, photoURL: profile.image_url }}
                size="xl"
                className="business-public-avatar"
              />
            )}
          </div>
          <div className="business-public-hero-text">
            <h1>{profile.name}</h1>
            <p className="business-public-meta">
              {profile.type && <span>{profile.type}</span>}
              {profile.industry && <span>· {profile.industry}</span>}
              {profile.region && <span>· {profile.region}</span>}
            </p>
            {profile.short_description && (
              <p className="business-public-description">
                {profile.short_description}
              </p>
            )}
            <p className="business-public-meta">
              {profile.email && <span>{profile.email}</span>}
              {profile.phone && <span>· {profile.phone}</span>}
            </p>
          </div>
        </section>

        <section className="business-public-ratings">
          <div className="rating-card">
            <div className="rating-label">
              {t('businessPublic.ratingOverall') || 'Overall'}
              {profile.rating_overall_raw != null && profile.rating_overall_raw !== profile.rating_overall && (
                <span style={{ fontSize: '0.75em', marginLeft: '0.5rem', color: 'rgba(148, 163, 184, 0.8)' }}>
                  ({t('businessPublic.fromReviews') || 'from reviews'}: {profile.rating_overall_raw.toFixed(1)})
                </span>
              )}
            </div>
            <div className="rating-value">
              {profile.rating_overall?.toFixed(1) || '0.0'} ★
            </div>
          </div>
          <div className="rating-card">
            <div className="rating-label">
              {t('businessPublic.ratingPaysOnTime') || 'Pays on time'}
            </div>
            <div className="rating-value">
              {profile.rating_pays_on_time?.toFixed(1) || '0.0'} ★
            </div>
          </div>
          <div className="rating-card">
            <div className="rating-label">
              {t('businessPublic.ratingWorkQuality') || 'Work quality'}
            </div>
            <div className="rating-value">
              {profile.rating_work_quality?.toFixed(1) || '0.0'} ★
            </div>
          </div>
          <div className="rating-card">
            <div className="rating-label">
              {t('businessPublic.ratingExperience') || 'Experience'}
            </div>
            <div className="rating-value">
              {profile.rating_experience?.toFixed(1) || '0.0'} ★
            </div>
          </div>
            {(profile.jobsCompleted != null || profile.xp != null || profile.level != null) && (
            <div className="rating-card">
              <div className="rating-label">
                {t('businessPublic.levelAndProgress') || 'Level & Progress'}
              </div>
              <div className="rating-value">
                {profile.level != null && (
                  <span>
                    {t('businessPublic.level') || 'Level'}: {profile.level}
                  </span>
                )}
                {profile.level != null && profile.xp != null && <br />}
                {profile.xp != null && (
                  <span>
                    XP: {profile.xp}
                  </span>
                )}
                {profile.xp != null && profile.jobsCompleted != null && <br />}
                {profile.jobsCompleted != null && (
                  <span style={{ fontSize: '0.85em', color: 'rgba(156, 163, 175, 0.95)' }}>
                    {t('businessPublic.jobsCompleted') || 'Jobs'}: {profile.jobsCompleted}
                  </span>
                )}
              </div>
            </div>
          )}
          {profile.level != null && (
            <div className="rating-card" style={{ gridColumn: '1 / -1', background: 'rgba(59, 130, 246, 0.1)', borderColor: 'rgba(59, 130, 246, 0.3)' }}>
              <div className="rating-label">
                {t('businessPublic.howToLevelUp') || 'How to Level Up'}
              </div>
              <div className="rating-value" style={{ fontSize: '0.9em', fontWeight: 'normal', lineHeight: '1.5' }}>
                {t('businessPublic.levelUpExplanation') || 'Complete jobs to earn XP! Each completed job gives 100 XP. Level up every 500 XP. Higher levels improve your overall rating.'}
              </div>
            </div>
          )}
        </section>

        <section className="business-public-reviews">
          <h2>
            {t('businessPublic.reviewsTitle') || 'Reviews'} (
            {profile.reviews_count || 0})
          </h2>
          {Array.isArray(profile.reviews) && profile.reviews.length > 0 ? (
            <ul className="business-public-reviews-list">
              {profile.reviews.map((r) => (
                <li key={r.id} className="business-public-review-item">
                  <div className="business-public-review-header">
                    <span className="review-overall">
                      {(r.rating_overall || 0).toFixed(1)} ★
                    </span>
                    <span className="review-date">
                      {r.created_at && r.created_at._seconds
                        ? new Date(r.created_at._seconds * 1000)
                            .toISOString()
                            .split('T')[0]
                        : ''}
                    </span>
                  </div>
                  {r.review_text && (
                    <p className="business-public-review-text">
                      {r.review_text}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="business-public-no-reviews">
              {t('businessPublic.noReviews') ||
                'No reviews yet. Be the first to work with this business.'}
            </p>
          )}
        </section>
      </main>
    </div>
  );
}


